import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DocumentListItem, LinkContext } from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { toast } from './Toast.js';

const CONTEXTS: { value: LinkContext; label: string }[] = [
  { value: 'source', label: 'Source' },
  { value: 'reference', label: 'Reference' },
  { value: 'requirement', label: 'Requirement' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'output', label: 'Output' },
  { value: 'attachment', label: 'Attachment' },
];

interface LinkedDocument {
  documentId: string;
  title: string;
  context: LinkContext;
  note?: string | null;
  createdAt: string;
  createdBy: string;
}

export function LinkedDocumentsPanel({
  workItemId,
  links,
  onNavigate,
}: {
  workItemId: string;
  links: LinkedDocument[];
  onNavigate: (p: string) => void;
}) {
  const qc = useQueryClient();
  const docsQ = useQuery({
    queryKey: ['documents', { all: true }],
    queryFn: () => apiList<DocumentListItem>('/documents', { query: { limit: 200 } }),
  });

  const [picking, setPicking] = useState(false);
  const [pickDocId, setPickDocId] = useState('');
  const [pickContext, setPickContext] = useState<LinkContext>('reference');
  const [pickNote, setPickNote] = useState('');

  const addLink = useMutation({
    mutationFn: () =>
      apiSingle(`/workitems/${workItemId}/links/documents`, {
        method: 'POST',
        body: { documentId: pickDocId, context: pickContext, note: pickNote || undefined },
      }),
    onSuccess: () => {
      toast.success('Linked');
      setPicking(false);
      setPickDocId('');
      setPickNote('');
      qc.invalidateQueries({ queryKey: ['workitem', workItemId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const existingIds = new Set(links.map((l) => l.documentId));
  const linkableDocs = (docsQ.data?.data ?? []).filter((d) => !existingIds.has(d.id));

  return (
    <div style={{ background: 'var(--surface-raised)', padding: 16, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h4 style={{ margin: 0 }}>Linked documents</h4>
        <button className="btn btn--ghost" onClick={() => setPicking((v) => !v)} style={{ fontSize: 12 }}>
          {picking ? 'Cancel' : '+ Link'}
        </button>
      </div>

      {picking && (
        <div style={{ background: 'var(--surface-sunken)', padding: 8, borderRadius: 4, marginBottom: 8 }}>
          <div className="field" style={{ marginBottom: 6 }}>
            <label htmlFor="ld-doc">Document</label>
            <select
              id="ld-doc"
              value={pickDocId}
              onChange={(e) => setPickDocId(e.target.value)}
              style={{ width: '100%', padding: 4 }}
            >
              <option value="">Choose a document…</option>
              {linkableDocs.map((d) => (
                <option key={d.id} value={d.id}>{d.title}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 6 }}>
            <label htmlFor="ld-ctx">Context</label>
            <select
              id="ld-ctx"
              value={pickContext}
              onChange={(e) => setPickContext(e.target.value as LinkContext)}
              style={{ width: '100%', padding: 4 }}
            >
              {CONTEXTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 6 }}>
            <label htmlFor="ld-note">Note (optional)</label>
            <input id="ld-note" value={pickNote} onChange={(e) => setPickNote(e.target.value)} style={{ width: '100%' }} />
          </div>
          <button
            className="btn btn--primary"
            disabled={!pickDocId || addLink.isPending}
            onClick={() => addLink.mutate()}
            style={{ width: '100%' }}
          >
            {addLink.isPending ? 'Linking…' : 'Link'}
          </button>
        </div>
      )}

      {links.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No documents linked yet.</div>}
      {links.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {links.map((l) => (
            <div key={l.documentId} style={{ padding: 6, border: '1px solid var(--border-subtle)', borderRadius: 4 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{l.context}</div>
              <a
                href={`/documents/${l.documentId}`}
                onClick={(e) => { e.preventDefault(); onNavigate(`/documents/${l.documentId}`); }}
              >
                📄 Document
              </a>
              {l.note && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>“{l.note}”</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
